import { Star } from "lucide-react";
import useScrollReveal from "@/hooks/useScrollReveal";

const TestimonialsSection = () => {
  const sectionRef = useScrollReveal();

  const testimonials = [
    {
      name: "Geert B.",
      location: "Krefeld",
      text: "Schnelle Antwort auf meine E-Mail, Vorführung bei uns zu Hause mit mitgebrachten Zutaten. Hervorragend gekocht – z.B. Hähnchencurry mit Spinat und Basmatireis. Auch nach dem Kauf immer erreichbar und hilfsbereit. Durch das frische Kochen hat sich sogar unsere Gesundheit verbessert!",
      rating: 5,
    },
    {
      name: "Weronika Z.",
      location: "Krefeld",
      text: "Trotz später Kontaktaufnahme sofort reagiert! Die Bestellung meines TM6 lief reibungslos, Lieferung innerhalb einer Woche. Beim Erlebniskochen war Jennifer mit vollem Enthusiasmus dabei. Sie hat sogar meiner Mutter beim Upgrade vom TM31 auf den TM6 geholfen.",
      rating: 5,
    },
    {
      name: "Birgit L.",
      location: "Krefeld",
      text: "Professionelle und sympathische Einweisung in das neueste Thermomix-Modell mit all seinen innovativen Funktionen. Jennifer ist auch nach der Schulung jederzeit für Fragen erreichbar. Absolute Empfehlung!",
      rating: 5,
    },
  ];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="section-padding bg-stone-50 relative overflow-hidden"
      ref={sectionRef}
    >
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 w-[600px] h-[600px] bg-emerald-100/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />

      <div className="container-custom relative">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 reveal">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4">
            Kundenstimmen
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6 font-[Outfit]">
            Kundenstimmen aus Krefeld & Umgebung
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed">
            Echte Erfahrungen von Menschen, die eine Vorführung erlebt haben.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-6 stagger-children">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              data-testid={`testimonial-${index}`}
              className="testimonial-card hover-lift"
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4 pt-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                ))}
              </div>

              {/* Quote */}
              <p className="text-slate-700 leading-relaxed mb-6">
                {testimonial.text}
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 pt-4 border-t border-stone-200/50">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold text-sm">
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{testimonial.name}</div>
                  <div className="text-sm text-slate-500">{testimonial.location}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Google Review CTA */}
        <div className="mt-14 reveal flex flex-col items-center">
          <a
            href="https://g.page/r/CQq6f5GvBulMEAE/review"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-review-button"
            aria-label="Jetzt eine Google-Bewertung schreiben"
            className="group relative inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white border border-stone-200 shadow-lg shadow-amber-100/40 hover:shadow-xl hover:shadow-amber-200/50 hover:-translate-y-0.5 transition-all duration-300"
          >
            <span className="relative flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 shadow-md">
              <Star className="w-5 h-5 text-white fill-white" />
              <span className="absolute inset-0 rounded-full bg-amber-400 opacity-0 group-hover:opacity-30 group-hover:scale-125 transition-all duration-500" />
            </span>
            <span className="flex flex-col leading-tight text-left">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-amber-600">Auf Google bewerten</span>
              <span className="text-base font-bold text-slate-900 font-[Outfit]">Jetzt Rezension schreiben</span>
            </span>
            <span className="flex gap-0.5 ml-1">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star
                  key={i}
                  className="w-3.5 h-3.5 text-amber-400 fill-amber-400 group-hover:scale-110 transition-transform"
                  style={{ transitionDelay: `${i * 40}ms` }}
                />
              ))}
            </span>
          </a>
          <p className="text-center text-sm text-slate-400 mt-5">
            Du hattest selbst eine Vorführung? Freu mich über deinen Stern! ⭐
          </p>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
